import { SKIP_STATIC_PAGE_GENERATION } from '@dreamstack/accentro-contentful/src/config'
import {
  BodyText,
  BodyTextWithLinks,
  EnergyCertificate,
  getFilteredPropOrProjectImages,
  GridWithGaps,
  HeaderImage,
  HeadingH1,
  MainLayout,
  MoreFlatsList,
  NearbyProperties,
  ProjectImageMap,
  ProjectPageBlocks,
  PropertyContact,
  PropertyImageSlider,
  PropertyMap,
  SimpleAccordion,
  SimpleAccordionEntry,
  transformPropstackImageUrl,
  useParams,
  VirtualRoundtrip,
} from '@dreamstack/feature-components'
import { ProjectInformation } from '@dreamstack/feature-components/src/property/ProjectInformation'
import type { IOtherLocaleUrlsContext } from '@dreamstack/i18n'
import {
  defaultLocale,
  OtherLocaleUrlsContextProvider,
  useLocale,
  useTranslation,
} from '@dreamstack/i18n'
import {
  queryGetProjectImageMapById,
  queryGetProjectLinkSectionById,
  useGetProjectImageMapByIdQuery,
  useGetProjectLinkSectionByIdQuery,
} from '@dreamstack/investors-graphql'
import { DreamstackSeo, ProjectRichMarkup } from '@dreamstack/seo'
import { AdditionalTrackingInfoContextProvider } from '@dreamstack/tracking'
import { trpc } from '@dreamstack/trpc/client'
import { PROPERTY_STATUS_FOLLOW_INDEX } from '@dreamstack/web-graphql'
import 'keen-slider/keen-slider.min.css'
import filter from 'lodash/filter'
import first from 'lodash/first'
import includes from 'lodash/includes'
import map from 'lodash/map'
import some from 'lodash/some'
import uniqBy from 'lodash/uniqBy'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import 'twin.macro'
import tw from 'twin.macro'
import { z } from 'zod'
import { defaultQueries } from '../../lib/defaultQueries'
import { getStaticPathsPlus } from '../../lib/getStaticPathsPlus'
import { getStaticPropsPlus } from '../../lib/getStaticPropsPlus'
import {
  getProjectById,
  getProjectsForOverview,
} from '../../lib/prisma/projects'
import type { EnergyCertificate as PropertyEnergyCertificate } from '../../lib/prisma/properties'

const StyledGridWithGaps = styled(GridWithGaps)<{ characterCount: number }>(
  ({ characterCount }) => [
    tw`col-span-12 bg-accentroGray-full text-white md:(margin-top[-100px]) ml:(-mt-px-128)`,
    characterCount <= 40 && tw`-mt-px-196 `,
    characterCount <= 60 && characterCount > 40 && tw`-mt-px-160 `,
    characterCount > 60 &&
      tw`-mt-px-128 ml:(margin-top[-130px]) lg:(-mt-px-80)`,
  ]
)

const paramSchema = z.object({ projectId: z.coerce.number() })

// TODO: Place somewhere else.
// const fallbackImageUrl =
//   'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1459&q=80'

const ProjectDetails: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  // get projectId from useRouter()
  // because props from getStaticProps are undefined during rendering
  // https://github.com/trpc/trpc/issues/596
  const params = useParams()
  const parsedParams = paramSchema.safeParse(params)

  const id = parsedParams.success ? parsedParams.data.projectId : null

  const locale = useLocale()
  const { data: project } = trpc.project.getProjectById.useQuery(
    { id: id!, locale },
    { trpc: { ssr: true }, staleTime: Infinity, enabled: !!id }
  )

  const { data: projectLinkSectionData } = useGetProjectLinkSectionByIdQuery({
    variables: { projectId: id },
  })

  const { data: projectImageMapData } = useGetProjectImageMapByIdQuery({
    variables: { projectId: id },
  })
  const projectImageMap = first(projectImageMapData?.imageMap?.items)

  const t = useTranslation()

  const projectLinkSection = first(
    projectLinkSectionData?.projectLinkSection?.items
  )

  const properties = project?.properties ?? []

  const firstProp = first(properties)

  const { data: topProps } = trpc.property.getTopPropertiesByProjectId.useQuery(
    {
      locale,
      region: firstProp?.region ?? undefined,
      rs_type: firstProp?.rs_type || 'APARTMENT',
      status: [1, 6, 40],
      projectId: project?.id,
    },
    { trpc: { ssr: true }, staleTime: Infinity }
  )

  const topProperties = topProps ?? []

  // TODO: ask propstack to implement thumb etc for LogoUrl
  const thumbImageUrl = transformPropstackImageUrl({
    url: project?.logo_url,
    size: 'default',
  })

  const propTitle = project?.title || project?.name
  const characterCount = propTitle?.length || 0

  const showTopProps = true
  const showMoreFlats = true

  const otherLocaleUrls: IOtherLocaleUrlsContext = {
    de: { url: `projekte/${id}` },
    en: { url: `projects/${id}` },
  }

  const { filteredImages } = getFilteredPropOrProjectImages(
    project?.project_images ?? []
  )
  //this is to get the first image out of the array for the HeaderImage
  const imagesWithoutFirstImage = filteredImages.slice(1)

  const hasPrice = some(properties, (p) => !(p.price == null))

  const filteredEnergyCertificates: Partial<PropertyEnergyCertificate>[] =
    filter(properties, (p) => !!p.thermal_characteristic)

  const energyCertificates = uniqBy(
    filteredEnergyCertificates,
    (p) => p.thermal_characteristic
  )
  const hasMultipleCertificates = energyCertificates.length > 1
  const propWithGeoLocation = first(filter(properties, (p) => !!p.geoLocation))

  const noFollowNoIndex = !some(properties, (p) =>
    includes(PROPERTY_STATUS_FOLLOW_INDEX, p?.status)
  )

  const showCertificates = false

  if (!id) return null

  return (
    <>
      <OtherLocaleUrlsContextProvider context={otherLocaleUrls}>
        <AdditionalTrackingInfoContextProvider context={{ projectId: id }}>
          <DreamstackSeo
            title={propTitle}
            description={propTitle}
            image={{ url: thumbImageUrl ?? '' }}
            nofollow={noFollowNoIndex}
            noindex={noFollowNoIndex}
          />
          {!!project && (
            <ProjectRichMarkup properties={properties} project={project} />
          )}
          <HeaderImage
            title={propTitle ?? ''}
            image={filteredImages[0]}
            smallFont
          />
          <MainLayout
            navbarProps={{
              forceDarkTextColor: false,
              isProjectAndProperty: true,
            }}
          >
            <GridWithGaps>
              <StyledGridWithGaps characterCount={characterCount}>
                <div tw="col-span-full flex flex-col">
                  {project && (
                    <ProjectInformation
                      properties={properties}
                      project={project}
                    />
                  )}
                </div>
                <PropertyContact
                  broker_avatar_url={project?.broker_avatar_url}
                  broker_first_name={project?.broker_first_name}
                  broker_last_name={project?.broker_last_name}
                  small={true}
                  morePadding={true}
                />
              </StyledGridWithGaps>
              <PropertyImageSlider
                hasPrice={hasPrice}
                images={imagesWithoutFirstImage}
              />
              <ProjectPageBlocks
                projectId={id}
                blockField="blocksBelowImages"
              />
              {projectLinkSection && (
                <VirtualRoundtrip projectLinkSection={projectLinkSection} />
              )}
              {/* TODO: implement proper spacing between blocks: Caveats: images have mb already the other two blocks not...
               <div tw="mb-px-48 ml:mb-px-96"></div> */}
              <div tw="col-span-12">
                <SimpleAccordion>
                  {!!project?.description_note && (
                    <SimpleAccordionEntry title={t('accentro:description')}>
                      <BodyTextWithLinks>
                        {project.description_note}
                      </BodyTextWithLinks>
                    </SimpleAccordionEntry>
                  )}
                  {!!project?.location_note && (
                    <SimpleAccordionEntry
                      title={t('accentro:location_lage')}
                      className="location-accordion"
                    >
                      <BodyTextWithLinks>
                        {project.location_note}
                      </BodyTextWithLinks>
                      <div tw="absolute left-0 right-0 mt-px-16">
                        {!!propWithGeoLocation && (
                          <PropertyMap property={propWithGeoLocation} />
                        )}
                      </div>
                      <div tw="height[467px] md:(height[368px]) ml:(height[567px])" />
                    </SimpleAccordionEntry>
                  )}
                  {!!project?.furnishing_note && (
                    <SimpleAccordionEntry title={t('accentro:furnishing')}>
                      <BodyTextWithLinks>
                        {project.furnishing_note}
                      </BodyTextWithLinks>
                    </SimpleAccordionEntry>
                  )}
                  {showCertificates ? (
                    map(energyCertificates, (certificate, i) => {
                      const certificateNumber = i + 1
                      const title = hasMultipleCertificates
                        ? `${t(
                            'accentro:property.energyCertificate'
                          )} #${certificateNumber}`
                        : t('accentro:property.energyCertificate')
                      return (
                        <SimpleAccordionEntry title={title}>
                          <EnergyCertificate certificate={certificate} />
                        </SimpleAccordionEntry>
                      )
                    })
                  ) : (
                    <SimpleAccordionEntry
                      title={t('accentro:property.energyCertificate')}
                    >
                      <BodyText>
                        {t('accentro:project.showNoCertificatesText')}
                      </BodyText>
                    </SimpleAccordionEntry>
                  )}
                </SimpleAccordion>
              </div>
              {/* TODO: Do we need document section in project? */}
              {/* <PropertyDocuments floorPlan={floorPlan} /> */}
              {projectImageMap && (
                <ProjectImageMap
                  projectImageMap={projectImageMap}
                  properties={properties}
                />
              )}
              {showMoreFlats && (
                <>
                  <HeadingH1 tw="col-span-12 mt-px-80 md:mt-px-128 lg:(mt-px-128)">
                    {t('accentro:propertyDetails.more-in-property')}
                  </HeadingH1>
                  <MoreFlatsList properties={properties} />
                </>
              )}
              {showTopProps && topProperties.length > 0 && (
                <>
                  <HeadingH1 tw="col-span-12 mt-px-80 md:mt-px-128 lg:(mt-px-128)">
                    {t('accentro:propertyDetails.more-in-region')}
                  </HeadingH1>
                  <NearbyProperties properties={topProperties} />
                </>
              )}
            </GridWithGaps>
          </MainLayout>
        </AdditionalTrackingInfoContextProvider>
      </OtherLocaleUrlsContextProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsPlus({
  queries: defaultQueries,
  getProps: async ({ params, apolloClient }) => {
    const projectId = params?.projectId as string | undefined
    const id = !!projectId ? parseInt(projectId) : undefined
    if (typeof id !== 'number' || isNaN(id)) {
      return { notFound: true }
    }

    const project = await getProjectById({
      id,
      locale: 'de',
    })

    const notFound = !project || project.markedAsComingSoon

    if (notFound) return { notFound }

    await queryGetProjectImageMapById({
      apolloClient,
      variables: {
        projectId: id,
      },
    })

    await queryGetProjectLinkSectionById({
      apolloClient,
      variables: {
        projectId: id,
      },
    })
  },
})

export const getStaticPaths = getStaticPathsPlus({
  getPaths: async () => {
    if (SKIP_STATIC_PAGE_GENERATION) {
      return []
    }
    const projects = await getProjectsForOverview({
      locale: defaultLocale,
    })
    return projects.map((x) => {
      return {
        params: { projectId: '' + x.id },
      }
    })
  },
  fallback: 'blocking',
})

export default ProjectDetails
